import { SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import { useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';
import {
  ExportConnectionEnum,
  FileTypeEnum,
  useGetExportConnectionsQuery,
  useExportCohortMutation,
} from '@verantos-dev/data-access-export-connections';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { ExportCohortPayloadType, ExportConnectionDetailsType } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const getExportLocationDisplayText = (connection: ExportConnectionDetailsType) => {
  if (!connection) {
    return '';
  }
  switch (connection.connectionType) {
    case ExportConnectionEnum.SNOWFLAKE:
      return `Snowflake: ${connection.name}`;

    case ExportConnectionEnum.S3:
      return `s3://${connection.connectionDetails.bucket}/${connection.connectionDetails.region}`;

    default:
      return connection.name;
  }
};

export const useExportCohortModal = () => {
  const { itemId, closeDialog } = useCohortModalContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [exportLocationId, setExportLocationId] = useState<ExportConnectionEnum>();
  const [schemaName, setSchemaName] = useState<string>('');
  const {
    user: { organizationId },
  } = useAuth();

  const { data, isLoading: loadingConnections } = useGetExportConnectionsQuery({
    organizationId: organizationId,
  });
  const { data: projectData, isLoading: loadingProject } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  });
  const { data: cohort, isLoading: loadingCohort } = useCohortProfileQuery({
    cohortId: itemId || '',
    projectId: projectId || '',
  });
  const [exportCohortMutation, { isLoading, error: exportCohortError }] = useExportCohortMutation();

  useEffect(() => {
    if (cohort && projectData?.data) {
      const _concatText = `${projectData.data.projectName}_${cohort.cohortName}`;
      const schemaProjectCohortText = _concatText.toUpperCase().replace(/[^a-zA-Z0-9]/g, '_');
      const todayDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      setSchemaName(`${schemaProjectCohortText}_${todayDate}`);
    }
  }, [cohort, projectData?.data]);

  const exportLocationOptions = useMemo(() => {
    if (!data) return [];
    return data.map((connection) => ({
      value: connection.id,
      displayValue: getExportLocationDisplayText(connection),
    }));
  }, [data]);

  const selectedExportLocation = useMemo(() => {
    return data?.find((connection) => connection.id === exportLocationId);
  }, [data, exportLocationId]);

  const onChangeExportLocationId = (event: SelectChangeEvent<unknown>) => {
    setExportLocationId(event.target.value as ExportConnectionEnum);
  };

  const onChangeSchemaName = (_: string, value: string) => {
    setSchemaName(value);
  };

  const onSubmit = async () => {
    if (projectId && itemId) {
      const connectorId = exportLocationId as string;
      let data: ExportCohortPayloadType['data'];
      if (selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE) {
        setSchemaName((prev) => prev.toUpperCase());
        data = {
          connectorId,
          connectorType: ExportConnectionEnum.SNOWFLAKE,
          connectorConfigurations: {
            targetSchemaName: schemaName.toUpperCase(),
          },
        };
      } else if (selectedExportLocation?.connectionType === ExportConnectionEnum.S3) {
        data = {
          connectorId,
          connectorType: ExportConnectionEnum.S3,
          connectorConfigurations: { fileType: FileTypeEnum.PARQUET },
        };
      } else {
        return;
      }
      await exportCohortMutation({
        projectId: projectId,
        cohortId: itemId,
        data,
      });
    }
  };

  const isValidForSubmit = Boolean(
    !(loadingCohort || loadingProject || loadingConnections) &&
      exportLocationId &&
      ((selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE && schemaName) ||
        selectedExportLocation?.connectionType === ExportConnectionEnum.S3)
  );

  return {
    isInitialLoading: loadingCohort || loadingProject || loadingConnections,
    isSubmitting: isLoading,
    isValidForSubmit,
    onClose: closeDialog,
    onSubmit,
    exportCohortError,

    projectName: projectData?.data?.projectName,
    cohortName: cohort?.cohortName,

    exportLocationId,
    onChangeExportLocationId,
    selectedExportLocation,

    exportLocationOptions,
    schemaName,
    onChangeSchemaName,
  };
};
